import React, { useMemo } from 'react';
import './PasswordStrength.css';

const PASSWORD_REQUIREMENTS = [
  { id: 'length', label: 'At least 8 characters', test: (pwd) => pwd.length >= 8 },
  { id: 'number', label: 'One number', test: (pwd) => /[0-9]/.test(pwd) },
  { id: 'special', label: 'One special character (!@#$%^&*)', test: (pwd) => /[!@#$%^&*(),.?":{}|<>]/.test(pwd) },
];

const PasswordStrength = ({ password, showRequirements = true }) => {
  const analysis = useMemo(() => {
    const results = PASSWORD_REQUIREMENTS.map((req) => ({
      ...req,
      passed: req.test(password || ''),
    }));

    const passedCount = results.filter((r) => r.passed).length;
    const totalRequirements = PASSWORD_REQUIREMENTS.length;

    let strength = 'weak';
    let strengthLabel = 'Weak';
    let strengthColor = '#dc3545';

    if (passedCount >= totalRequirements) {
      strength = 'strong';
      strengthLabel = 'Strong';
      strengthColor = '#10B981';
    } else if (passedCount >= 3) {
      strength = 'medium';
      strengthLabel = 'Medium';
      strengthColor = '#F59E0B';
    }

    return {
      results,
      passedCount,
      totalRequirements,
      strength,
      strengthLabel,
      strengthColor,
      percentage: (passedCount / totalRequirements) * 100,
    };
  }, [password]);

  if (!password) {
    return null;
  }

  return (
    <div className="password-strength">
      {/* Strength Bar */}
      <div className="strength-bar-container">
        <div className="strength-bar">
          <div
            className={`strength-bar-fill ${analysis.strength}`}
            style={{ width: `${analysis.percentage}%` }}
          />
        </div>
        <span
          className="strength-label"
          style={{ color: analysis.strengthColor }}
        >
          {analysis.strengthLabel}
        </span>
      </div>

      {/* Requirements Checklist */}
      {showRequirements && (
        <div className="requirements-list">
          {analysis.results.map((req) => (
            <div
              key={req.id}
              className={`requirement-item ${req.passed ? 'passed' : ''}`}
            >
              <i className={`bi ${req.passed ? 'bi-check-circle-fill' : 'bi-circle'}`}></i>
              <span>{req.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Export validation function for use in forms
export const validatePassword = (password) => {
  const results = PASSWORD_REQUIREMENTS.map((req) => ({
    id: req.id,
    passed: req.test(password || ''),
  }));

  const allPassed = results.every((r) => r.passed);
  const errors = results.filter((r) => !r.passed);

  return {
    isValid: allPassed,
    errors: errors.map((e) => {
      const req = PASSWORD_REQUIREMENTS.find((r) => r.id === e.id);
      return req?.label || '';
    }),
  };
};

export default PasswordStrength;
